/* The one bar across the top: brand · DTF / UV DTF · sheet size · sheets ·
   name + save · undo/redo · live price · Save · Review & order · more. */

import { useState } from 'react';
import { formatMoney } from '@gsb/core';
import { useStore, rename, flushSave, undo, redo, toast, startFresh, emailOn, openEmailFlow } from '../store/store';
import { Icon } from '../ui/icons';
import { MenuButton, MenuItem } from '../ui/primitives';
import { useEstimate } from '../ui/Panels';
import { HelpDialog } from '../ui/Header';
import { navigate } from '../router';
import { TransferSeg, SheetChoiceSelect, SaveStatus } from './controls';
import { NewGangsheetButton } from './StartFresh';
import { SaveButton, SaveEmailDialog } from '../ui/Email';

export function TopBar({ onReview }: { onReview: () => void }) {
  const name = useStore(s => s.doc.name);
  const sheets = useStore(s => s.doc.sheets.length);
  const pid = useStore(s => s.projectId);
  const past = useStore(s => s.past.length);
  const future = useStore(s => s.future.length);
  const links = useStore(s => s.config?.links);
  const est = useEstimate();
  const [help, setHelp] = useState(false);
  const [saveSheet, setSaveSheet] = useState(false);
  const email = useStore(s => s.config?.email?.mode === 'on');
  const savedTo = useStore(s => s.meta.savedTo);
  const site = links?.site || 'https://printees.co.nz';
  const save = async () => {
    if (!pid) { toast('Add a design and your gangsheet saves itself from then on.'); return; }
    await flushSave();
    const st = useStore.getState().save.state;
    if (st === 'saved') toast('Saved. Find it any time in My gang sheets.');
  };
  return (
    <header className="top">
      <a className="top-logo" href={site} aria-label="Printees home" onClick={() => void flushSave()}>
        <img src="/brand/printees-logo-on-light.webp" alt="Printees" width={124} height={28} />
      </a>
      <span className="top-app">Gangsheet<br />Builder</span>
      <NewGangsheetButton variant="top" />
      <span className="top-sep no-phone" aria-hidden="true" />
      <div className="top-set no-phone">
        <TransferSeg />
        <SheetChoiceSelect bare />
        {sheets > 0 && <span className="top-count" title="Sheets in this gangsheet"><Icon name="sheets" /><b className="num">{sheets}</b><span className="lbl">{sheets === 1 ? 'sheet' : 'sheets'}</span></span>}
      </div>
      <div className="top-name no-phone">
        <input aria-label="Gangsheet name" value={name} maxLength={120} onChange={e => rename(e.target.value)} onBlur={() => void flushSave()} />
        <SaveStatus />
      </div>
      <span className="grow" />
      <div className="top-hist no-phone">
        <button className="ibtn" onClick={undo} disabled={!past} aria-label="Undo" data-tip="Undo (Ctrl/⌘ Z)"><Icon name="undo" /></button>
        <button className="ibtn" onClick={redo} disabled={!future} aria-label="Redo" data-tip="Redo (Ctrl/⌘ ⇧ Z)"><Icon name="redo" /></button>
      </div>
      <div className="top-price" aria-live="polite" title={est?.built ? 'The price for your gangsheet, GST included' : 'An estimate until you build your gangsheet, GST included'}>
        <span>{est?.built ? 'Price' : 'Estimate'} · GST incl.</span>
        <b className="num">{formatMoney(est ? est.total : 0)}</b>
      </div>
      {email ? <SaveButton /> : <button className="btn btn-line btn-sm top-save no-phone" onClick={() => void save()} aria-label="Save" data-tip="Saves automatically as you go. Click to save now"><Icon name="save" /><span className="lbl">Save</span></button>}
      <button className="btn btn-primary btn-sm top-order" onClick={onReview} title="Check everything, then order"><span className="no-phone">Review &amp; order</span><span className="only-phone">Order</span><Icon name="arrowRight" className="ico-arrow" /></button>
      <MenuButton align="right" button={(open, toggle) => <button className="ibtn" aria-label="More" aria-expanded={open} onClick={toggle}><Icon name="menu" /></button>}>{close => (
        <>
          <MenuItem icon="plus" label="New gangsheet" onClick={() => { close(); void startFresh(); }} />
          <hr />
          <MenuItem icon="undo" label="Undo" kbd="Ctrl Z" onClick={() => { undo(); close(); }} disabled={!past} />
          <MenuItem icon="redo" label="Redo" kbd="Ctrl ⇧ Z" onClick={() => { redo(); close(); }} disabled={!future} />
          <hr />
          <MenuItem icon="eye" label="Print preview" onClick={() => { useStore.setState({ preview: true }); close(); }} disabled={!sheets} />
          <MenuItem icon="help" label="Help & shortcuts" onClick={() => { setHelp(true); close(); }} />
          <hr />
          <MenuItem icon="sheets" label="My gang sheets" onClick={() => { void flushSave(); navigate('/my'); close(); }} />
          {email && emailOn() && <MenuItem icon="mail" label={savedTo ? 'Saved to ' + savedTo : 'Save & Email My Gangsheet'} onClick={() => { close(); void flushSave(); setSaveSheet(true); }} />}
          {email && <MenuItem icon="search" label="Find My Gangsheets" onClick={() => { close(); void flushSave(); openEmailFlow('find'); }} />}
          <hr />
          {links && <a href={links.uploadPage} onClick={() => void flushSave()}><Icon name="upload" />Upload a finished sheet instead</a>}
          {links && <a href={links.guide} onClick={() => void flushSave()}><Icon name="exit" />Exit to printees.co.nz</a>}
        </>
      )}</MenuButton>
      {help && <HelpDialog onClose={() => setHelp(false)} />}
      {saveSheet && <SaveEmailDialog onClose={() => setSaveSheet(false)} />}
    </header>
  );
}
